import apiClient from './apiClient';
import type { ApiResponse } from '../types';
import type {
  ReconciliationResponse,
  ReconciliationSummaryResponse,
} from '../types/reconciliation';

const API_TYPE = 'api' as const;

// Module-level cache for the lightweight summary endpoint. Keeps the
// Schedule-page pulse from re-firing N broker calls every time the user
// navigates back to /schedule. TTL chosen to match the plan's 5-minute
// refresh window; users can manually refresh from the pill.
const SUMMARY_TTL_MS = 5 * 60 * 1000;

let _summaryCache: { data: ReconciliationSummaryResponse; cachedAt: number } | null = null;
let _summaryInflight: Promise<ReconciliationSummaryResponse> | null = null;

function unwrap<T>(response: ApiResponse<T> | T, defaultMessage: string): T {
  if ((response as any)?.success !== undefined) {
    const wrapped = response as ApiResponse<T>;
    if (!wrapped.success || !wrapped.data) {
      throw new Error(wrapped.message || defaultMessage);
    }
    return wrapped.data;
  }
  return response as T;
}

class ReconciliationService {
  async getReconciliation(params: { broker: string; clientId: string }): Promise<ReconciliationResponse> {
    const qs = `?broker=${encodeURIComponent(params.broker)}&client_id=${encodeURIComponent(params.clientId)}`;
    try {
      const response = await apiClient.get<ApiResponse<ReconciliationResponse> | ReconciliationResponse>(
        `/reconciliation${qs}`,
        undefined,
        API_TYPE,
      );
      return unwrap(response, 'Failed to fetch reconciliation');
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch reconciliation');
    }
  }

  /**
   * Aggregate reconciliation status across every enabled broker account.
   *
   * - When `force` is false (default), returns cached data if it's still
   *   within the TTL window. If a fetch is in flight, the in-flight
   *   promise is returned so concurrent callers share one network roundtrip.
   * - When `force` is true, the cache is bypassed and the request fires
   *   immediately. The result still populates the cache.
   */
  async getSummary(force = false): Promise<ReconciliationSummaryResponse> {
    if (!force && _summaryCache && Date.now() - _summaryCache.cachedAt < SUMMARY_TTL_MS) {
      return _summaryCache.data;
    }
    if (_summaryInflight) {
      return _summaryInflight;
    }
    _summaryInflight = (async () => {
      try {
        const response = await apiClient.get<
          ApiResponse<ReconciliationSummaryResponse> | ReconciliationSummaryResponse
        >('/reconciliation/summary', undefined, API_TYPE);
        const data = unwrap(response, 'Failed to fetch reconciliation summary');
        _summaryCache = { data, cachedAt: Date.now() };
        return data;
      } catch (error: any) {
        throw new Error(
          error.response?.data?.message || error.message || 'Failed to fetch reconciliation summary',
        );
      } finally {
        _summaryInflight = null;
      }
    })();
    return _summaryInflight;
  }

  /** Drop the cached summary (e.g. on logout). */
  clearSummaryCache(): void {
    _summaryCache = null;
  }

  /** For tests / debug — true if a non-stale entry is in cache. */
  hasFreshSummary(): boolean {
    return !!_summaryCache && Date.now() - _summaryCache.cachedAt < SUMMARY_TTL_MS;
  }
}

const reconciliationService = new ReconciliationService();
export default reconciliationService;
export { ReconciliationService };
